import { Emitter } from "@core/events/emitter";
import type {
  IPendingImportsModel,
  PendingImport,
  PendingImportStatus,
} from "./IPendingImportsModel";

/**
 * `PendingImportsModel` — in-memory registry of in-flight model imports.
 * Local-only, mirrors `InMemoryToastsModel`. The hierarchy renders one loading
 * row per entry (see `usePendingImports` + `HierarchyView.vue`).
 */
export class PendingImportsModel implements IPendingImportsModel {
  readonly onChanged = new Emitter<ReadonlyArray<PendingImport>>();
  private m_pending: PendingImport[] = [];

  get pendingImports(): ReadonlyArray<PendingImport> {
    return this.m_pending;
  }

  begin(input: { id: string; displayName: string; sceneId?: string }): void {
    if (this.m_pending.some((p) => p.id === input.id)) return;
    this.m_pending = [
      ...this.m_pending,
      {
        id: input.id,
        displayName: input.displayName,
        sceneId: input.sceneId,
        progress: 0,
        status: "uploading",
      },
    ];
    this.onChanged.emit(this.m_pending);
  }

  update(id: string, progress: number, status?: PendingImportStatus): void {
    let mutated = false;
    this.m_pending = this.m_pending.map((p) => {
      if (p.id !== id) return p;
      mutated = true;
      return {
        ...p,
        progress: Math.min(1, Math.max(0, progress)),
        status: status ?? p.status,
      };
    });
    if (mutated) this.onChanged.emit(this.m_pending);
  }

  remove(id: string): void {
    const next = this.m_pending.filter((p) => p.id !== id);
    if (next.length === this.m_pending.length) return;
    this.m_pending = next;
    this.onChanged.emit(this.m_pending);
  }
}
